import React, {useState} from 'react'
import Card from 'organization/Event/AreaList/Card'
import AreasProvider, {Area, useAreas} from 'organization/Event/AreasProvider'
import {useEventRoutes} from 'organization/Event/EventRoutes'
import Page from 'organization/Event/Page'
import {
  CHECK_IN_ATTENDEES,
  EVENT_SETTINGS,
  START_AREAS,
  START_ROOMS,
  usePermissions,
} from 'organization/PermissionsProvider'
import PageHeader from 'lib/ui/PageHeader'
import Title from 'lib/ui/PageHeader/Title'
import HasPermission, {
  useHasAny as useHasAnyPermission,
} from 'organization/HasPermission'
import {RelativeLink} from 'lib/ui/link/RelativeLink'
import NewItemButton from 'lib/ui/PageHeader/NewItemButton'
import {StaticAreaProvider} from 'organization/Event/Area/AreaProvider'
import {ZoomAreasBreadcrumbs} from 'organization/Event/Page/PageBreadcrumbs'
import FullPageLoader from 'lib/ui/layout/FullPageLoader'
import styled from 'styled-components'
import Button from 'lib/ui/PageHeader/PageHeaderButton'
import {
  ExportEventLogsAlerts,
  ExportEventLogsMenu,
  ExportEventLogsProvider,
} from 'organization/Event/AreaList/ExportEventLogs'
import Select, {StyledSelect} from 'lib/ui/Select'
import Option from 'lib/ui/Select/Option'
import {DraggableOverlay} from 'lib/ui/drag-and-drop'
import {
  SortMode,
  useSetAreaPositions,
} from 'organization/Event/AreaList/use-set-area-positions'
import {onUnknownChangeHandler} from 'lib/dom'
import {DragDropContext, Draggable, Droppable} from 'react-beautiful-dnd'
import ErrorAlert from 'lib/ui/alerts/ErrorAlert'

export default function AreaList() {
  return (
    <ZoomAreasBreadcrumbs page="Zoom Areas">
      <Page>
        <AreasProvider>
          <Content />
        </AreasProvider>
      </Page>
    </ZoomAreasBreadcrumbs>
  )
}

function Content() {
  const routes = useEventRoutes()
  const [sortMode, setSortMode] = useState<SortMode>('custom')

  return (
    <ExportEventLogsProvider>
      <StyledPageHeader>
        <Title text="Zoom Areas" />
        <HeaderActions>
          <AreaSortSelect sortMode={sortMode} onChange={setSortMode} />
          <HasPermission required={EVENT_SETTINGS}>
            <RightButtons>
              <ExportEventLogsMenu />
              <RelativeLink disableStyles to={routes.areas.zoom_user_settings}>
                <Button
                  text="Zoom User Settings"
                  aria-label="zoom user settings"
                  variant="outlined"
                  color="primary"
                />
              </RelativeLink>
              <RelativeLink disableStyles to={routes.areas.create}>
                <NewItemButton text="New Area" aria-label="create area" />
              </RelativeLink>
            </RightButtons>
          </HasPermission>
        </HeaderActions>
      </StyledPageHeader>

      <ExportEventLogsAlerts />
      <Areas sortMode={sortMode} />
    </ExportEventLogsProvider>
  )
}

function Areas(props: {sortMode: SortMode}) {
  const {areas, loading} = useAreas()
  const {can} = usePermissions()

  if (loading || !areas) {
    return <FullPageLoader />
  }

  const visibleAreas = areas.filter((a) => {
    if (can(START_ROOMS) || can(EVENT_SETTINGS) || can(START_AREAS)) {
      return true
    }

    /**
     * If a user only has permission to check in attendees (tech check agent), then
     * we'll only show the tech check area.
     */
    if (can(CHECK_IN_ATTENDEES)) {
      return a.is_tech_check
    }

    return false
  })

  return (
    <div>
      <List areas={visibleAreas} sortMode={props.sortMode} />
    </div>
  )
}

function List(props: {areas: Area[]; sortMode: SortMode}) {
  const {areas, handleDrag, error} = useSetAreaPositions(
    props.areas,
    props.sortMode,
  )
  const hasAnyPermission = useHasAnyPermission()

  const isEmpty = areas.length === 0
  if (isEmpty) {
    return <div>No areas have been created</div>
  }

  const canSetAreaPositions = hasAnyPermission([EVENT_SETTINGS, START_AREAS])
  const isDraggable = props.sortMode === 'custom' && canSetAreaPositions
  if (!isDraggable) {
    return (
      <>
        {areas.map((a) => (
          <StaticAreaProvider area={a} key={a.id}>
            <Card />
          </StaticAreaProvider>
        ))}
      </>
    )
  }

  return (
    <>
      <ErrorAlert>{error}</ErrorAlert>
      <DragDropContext onDragEnd={handleDrag}>
        <Droppable droppableId="areas">
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              {areas.map((a, index) => (
                <Draggable draggableId={`${a.id}`} index={index} key={a.id}>
                  {(provided) => (
                    <div {...provided.draggableProps} ref={provided.innerRef}>
                      <DraggableOverlay>
                        <StaticAreaProvider area={a}>
                          <Card dragHandleProps={provided.dragHandleProps} />
                        </StaticAreaProvider>
                      </DraggableOverlay>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </>
  )
}

function AreaSortSelect(props: {
  sortMode: SortMode
  onChange: (mode: SortMode) => void
}) {
  return (
    <StyledAreaSortSelect
      value={props.sortMode}
      onChange={onUnknownChangeHandler(props.onChange)}
      aria-label="sort areas"
    >
      <Option value="custom" aria-label="sort custom">
        Custom Order
      </Option>
      <Option value="az" aria-label="sort a to z">
        A-Z
      </Option>
      <Option value="za" aria-label="sort z to a">
        Z-A
      </Option>
    </StyledAreaSortSelect>
  )
}

const StyledPageHeader = styled(PageHeader)`
  margin-bottom: ${(props) => props.theme.spacing[2]} !important;

  @media (min-width: ${(props) => props.theme.breakpoints.sm}) {
    margin-bottom: ${(props) => props.theme.spacing[3]} !important;
  }
`

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${(props) => props.theme.spacing[3]};
`

const RightButtons = styled.div`
  display: flex;
  gap: ${(props) => props.theme.spacing[2]};
`

const StyledAreaSortSelect = styled(Select)`
  margin-bottom: 0;
  min-width: 130px;

  ${StyledSelect} {
    margin-top: 0 !important;

    > div {
      padding: ${(props) => props.theme.spacing[2]}
        ${(props) => props.theme.spacing[3]};
      border-radius: 4px !important;
      border-color: ${(props) => props.theme.colors.border};
      font-size: 14px;
      line-height: 20px;
    }
  }
`
